import { useRouter } from 'next/router';

import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react';

import { getApp } from 'firebase/app';
import { doc, getDoc, getFirestore } from '@firebase/firestore';
import * as FirebaseAuth from '@firebase/auth';

import nookies from 'nookies';

import Member from 'models/Member.model';

import { AuthState, initialState, setFirebaseAuth, setFirebaseAuthSignOut, setMember } from 'store/slices/wineone-auth';

import {
  closeSignInModal as closeSignInModalAction,
  ModalsState,
  openSignInModal as openSignInModalAction
} from 'store/slices/global-modals';

import { useAppDispatch, useAppSelector } from 'store';
import { SESSION_TOKEN } from 'config';

import SignIn from 'pages-sections/sessions/Sign-in';

import { Dialog, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { User } from '@firebase/auth';

interface ContextProps {
  state: AuthState;
  /** 아이디 패스워드 로그인 */
  signInWithIdAndPassword: (cust_id: string, cust_pw: string) => Promise<{ success: boolean; error?: any }>;
  /** 커스텀 토큰 로그인 (카카오) */
  signInWithCustomToken: (customToken: string) => Promise<{ success: boolean; error?: any }>;
  /** Firebase auth 로그아웃 */
  signOut: () => Promise<void>;

  /** 아이디 패스워드 확인하기 */
  checkIdAndPassword: (cust_id: string, cust_pw: string) => Promise<{ success: boolean; error?: any }>;

  /** 전역 로그인 모달 열기 */
  openSignInModal: () => void;
  /** 전역 로그인 모달 닫기 */
  closeSignInModal: () => void;
}

const AuthContext = createContext<ContextProps>({
  state: initialState,
  signInWithIdAndPassword: async () => ({ success: false }),
  signInWithCustomToken: async (customToken: string) => ({ success: false }),
  signOut: async () => {},
  /** 아이디 패스워드 확인하기 */
  checkIdAndPassword: async () => ({ success: false }),
  /** 전역 로그인 모달 열기 */
  openSignInModal: () => void 0,
  /** 전역 로그인 모달 닫기 */
  closeSignInModal: () => void 0
});

// =======================================================
type AuthProviderProps = { children: ReactNode };
type SignedInUser = Pick<FirebaseAuth.User, 'uid' | 'displayName' | 'email' | 'emailVerified'> & {
  cust_id?: string;
};
// =======================================================

export const AuthProvider: FC<AuthProviderProps> = ({ children, ...rest }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const router = useRouter();

  const { state, modals }: { state: AuthState; modals: ModalsState } = useAppSelector((state) => {
    return {
      state: state['auth'],
      modals: state['modals']
    };
  });

  // URL이 변경될 때 마다 로그인 모달을 닫음
  router.events.on('routeChangeStart', () => {
    dispatch(closeSignInModalAction());
  });

  const contextValue: { state: AuthState } = useMemo(() => ({ state }), [state]);

  React.useEffect(() => {
    const auth = FirebaseAuth.getAuth();
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      // 코드 개선
      if (firebaseUser) {
        // 필요한 정보만
        firebaseUser.getIdTokenResult(true).then(async (idTokenResult) => {
          // Firebase authentication에 user record만 있고 회원정보는 없는 경우
          if (typeof idTokenResult.claims['user_id'] === 'undefined') {
            console.log('Firebase auth만 있고 사용자 정보는 없는 user record.', idTokenResult);
            dispatch(setFirebaseAuthSignOut());
            return;
          }

          const userRole = idTokenResult.claims['role'];

          if (userRole === 'user') {
            const user: SignedInUser = {
              uid: firebaseUser.uid,
              displayName: firebaseUser.displayName,
              email: firebaseUser.email,
              emailVerified: false
            };

            dispatch(setFirebaseAuth({ user }));
            dispatch(closeSignInModalAction());

            // firestore에서 회원정보 가져오기
            const member: Member = await getDoc(doc(getFirestore(getApp()), 'member', user.uid)).then((docSnap) => {
              const { _id: uid, use_fg, cust_nm, cust_id, cust_seq, ci_gbn_fg }: any = docSnap.data();
              return { uid, use_fg, cust_nm, cust_id, cust_seq, ci_gbn_fg };
            });

            // 앱 사용이 불가능한 회원 (정지된 회원)
            if (member.use_fg !== '0') {
              console.warn(`올바르지 않은 상태의 회원입니다.[state=${member.use_fg}]`);
              if (typeof window !== 'undefined')
                window.alert('서비스 이용가능한 상태가 아닌 계정정보\r\n[' + userRole + ']');
              dispatch(setFirebaseAuthSignOut());
              await signOut();
              return;
            }

            dispatch(setMember(member));
          } else {
            console.warn('[onAuthStateChanged] 회원 권한의 계정만 로그인 가능합니다.');
            if (typeof window !== 'undefined')
              window.alert('회원 권한의 사용자만 로그인이 가능합니다.\r\n[' + userRole + ']');
            dispatch(setFirebaseAuthSignOut());

            await signOut();
          }
        });
      } else {
        console.debug('[AuthContext] 비로그인 사용자');
        dispatch(setFirebaseAuthSignOut());
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).nookies = nookies;
    }
    return FirebaseAuth.getAuth().onIdTokenChanged(async (user) => {
      if (!user) {
        console.debug('[AuthContext] 세션토큰 제거#1');
        nookies.destroy(null, SESSION_TOKEN);
        return;
      }

      try {
        const { claims, token } = await user.getIdTokenResult(true);

        // role이 'user'가 아닐경우
        if (claims?.role !== 'user') {
          console.debug('[AuthContext] 세션토큰 제거#2');
          nookies.destroy(null, SESSION_TOKEN);
        } else {
          console.debug('[AuthContext] 세션토큰 갱신');
          nookies.set(null, SESSION_TOKEN, token, { path: '/' });
        }
      } catch (e) {
        console.warn('[AuthContext] getIdTokenResult 오류', e);
        await signOut();
        // window.alert('사용자 정보갱신 중 오류가 발생했습니다.\r\n다시 로그인해주세요.');
      }
    });
  }, []);

  // force refresh the token every 10 minutes
  React.useEffect(() => {
    const handle = setInterval(async () => {
      console.debug('[AuthContext] firebase authentication 자동갱신 시작...');
      try {
        const user = FirebaseAuth.getAuth().currentUser;
        if (user) await user.getIdToken(true);
      } catch (e) {
        console.warn('[AuthContext] firebase authentication 토큰갱신 실패', e);
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(handle);
  }, []);

  /**
   * 아이디 패스워드로 로그인 하기
   */
  const signInWithIdAndPassword = React.useCallback(
    async (cust_id: string, cust_pw: string): Promise<{ success: boolean; error?: any }> => {
      const auth = FirebaseAuth.getAuth();

      await FirebaseAuth.setPersistence(auth, FirebaseAuth.browserLocalPersistence);

      try {
        const email = `${cust_id}@normalnara.com`;
        const password = cust_pw;
        const userCredential: FirebaseAuth.UserCredential | any = await FirebaseAuth.signInWithEmailAndPassword(
          auth,
          email,
          password
        ).catch((error) => {
          console.warn('[FirebaseAuth.signInWithEmailAndPassword] error', error);
          return { error };
        });

        // 이메일, 비밀번호 로그인 실패
        if (userCredential.error) {
          return { success: false, error: userCredential.error };
        }

        console.log('[AuthContext] 이메일 로그인 성공');
        const { token } = await userCredential.user.getIdTokenResult(true);

        nookies.set(null, SESSION_TOKEN, token, { path: '/' });

        return { success: true };
      } catch (error) {
        console.error('[AuthContext][signInWithIdAndPassword] 로그인 처리 중 오류', error);
        return { success: false, error };
      }
    },
    []
  );

  /** 커스텀 토큰 로그인 */
  const signInWithCustomToken = React.useCallback(async (customToken: string) => {
    console.debug('[AuthContext] 커스텀 토큰 로그인');

    try {
      const userCredential = await FirebaseAuth.signInWithCustomToken(FirebaseAuth.getAuth(), customToken);
      const { token } = await userCredential.user.getIdTokenResult(true);

      nookies.set(null, SESSION_TOKEN, token, { path: '/' });

      return { success: true, provider: SESSION_TOKEN };
    } catch (error) {
      console.error('[AuthContext] 커스텀 토큰 로그인 실패', error);
      return { success: false, provider: 'apple', error };
    }
  }, []);

  /** 아이디 패스워드 확인하기 */
  const checkIdAndPassword = React.useCallback(
    async (email: string, cust_pw: string): Promise<{ success: boolean; error?: any }> => {
      const auth = FirebaseAuth.getAuth();

      // 현재 비밀번호 검사
      const credential = FirebaseAuth.EmailAuthProvider.credential(email, cust_pw);
      const checkCrendential = await FirebaseAuth.reauthenticateWithCredential(
        auth.currentUser as User,
        credential
      ).catch((error) => ({ error }));

      // @ts-ignore
      if (checkCrendential.error) {
        // @ts-ignore
        return { success: false, error: checkCrendential.error };
      } else {
        return { success: true };
      }
    },
    []
  );

  /**
   * 사용자 로그아웃
   */
  const signOut = React.useCallback(async (): Promise<void> => {
    const auth = FirebaseAuth.getAuth();
    try {
      nookies.destroy(null, SESSION_TOKEN, { path: '/' });
      await auth?.signOut();
      console.log('[AuthContext] 사용자 로그아웃 처리완료.');
    } catch (error) {
      console.warn('[AuthContext] 사용자 로그아웃 처리실패.');
    }
  }, []);

  /** 전역 로그인 모달 열기 */
  const openSignInModal = () => {
    dispatch(openSignInModalAction());
  };

  /** 전역 로그인 모달 닫기 */
  const closeSignInModal = () => {
    dispatch(closeSignInModalAction());
  };

  return (
    <AuthContext.Provider
      value={{
        ...contextValue,
        signInWithIdAndPassword,
        signInWithCustomToken,
        signOut,
        checkIdAndPassword,
        openSignInModal,
        closeSignInModal
      }}
    >
      {children}

      <Dialog
        scroll="body"
        open={modals.isSignInModalOpen}
        fullWidth={isSmallScreen ?? false}
        onClose={closeSignInModal}
      >
        <SignIn onSuccess={() => closeSignInModal()} targetUrl={router.asPath} />
      </Dialog>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext<ContextProps>(AuthContext);

export default AuthContext;
